import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    Save,
    Plus,
    X,
    Tag,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    CATEGORIES,
    emptyBlock,
    nextBlockLetter,
    normalizeBlock,
    emptyExercise,
} from "@/lib/workoutTaxonomy";
import BlockEditor from "@/components/workout/BlockEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red";

export default function WorkoutBuilder() {
    const { user, loading } = useAuth();
    const { workoutId } = useParams();
    const navigate = useNavigate();
    const isEdit = !!workoutId;

    const [busy, setBusy] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    // When validation fails we stash the offending coordinates so SetsTable can
    // render a red border around the bad input AND we can scroll/focus it.
    const [validationError, setValidationError] = useState(null);
    // Forces BlockEditor to switch its active-level tab to whichever level
    // contains the bad set — keyed by `blockIdx`. Setting a value increments
    // the version so BlockEditor's effect re-runs even on the same level.
    const [forceLevelByBlock, setForceLevelByBlock] = useState({});

    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("strength");
    const [estimatedDuration, setEstimatedDuration] = useState("");
    const [timesPerWeek, setTimesPerWeek] = useState("");
    const [tierRequired, setTierRequired] = useState("member");
    // body_focus only applies to category=strength. Persisted on the workout
    // doc so the athlete-side Strength page can sub-tab Full/Lower/Upper.
    const [bodyFocus, setBodyFocus] = useState("");
    const [subtabs, setSubtabs] = useState([]);
    const [subtabInput, setSubtabInput] = useState("");
    const [availableSubtabs, setAvailableSubtabs] = useState([]);
    const [blocks, setBlocks] = useState([emptyBlock("A", 1)]);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner" && user.role !== "coach") navigate("/dashboard", { replace: true });
    }, [loading, user, navigate]);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/workouts/${workoutId}`, { withCredentials: true });
                const w = r.data;
                setTitle(w.title || "");
                setCategory(w.category || "strength");
                setEstimatedDuration(w.estimated_duration ?? "");
                setTimesPerWeek(w.times_per_week ?? "");
                setTierRequired(w.tier_required || "member");
                setBodyFocus(w.body_focus || "");
                setSubtabs(w.subtabs || []);
                // Phase 1 supersets/circuits rebuild: server hydrates legacy
                // single-exercise blocks into the new `exercises` shape, but
                // we run normalizeBlock() defensively here too so a stale
                // cached response from before the server hydrator is harmless.
                const normalizedBlocks = (w.blocks || []).map(normalizeBlock);
                setBlocks(normalizedBlocks.length ? normalizedBlocks : [emptyBlock("A", 1)]);
            } catch (e) {
                toast.error("Couldn't load workout", { description: e?.response?.data?.detail });
                navigate("/coach/workouts", { replace: true });
            } finally {
                setBusy(false);
            }
        })();
    }, [isEdit, workoutId, navigate]);

    // Load category-scoped subtabs
    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/workout-subtabs`, {
                    params: { category }, withCredentials: true,
                });
                setAvailableSubtabs((r.data || []).map((s) => s.name));
            } catch { setAvailableSubtabs([]); }
        })();
    }, [category]);

    const addSubtab = async (name) => {
        const v = name.trim();
        if (!v) return;
        if (subtabs.includes(v)) return;
        setSubtabs([...subtabs, v]);
        if (!availableSubtabs.includes(v)) {
            try {
                await axios.post(`${API}/coach/workout-subtabs`, { category, name: v }, { withCredentials: true });
                setAvailableSubtabs([...availableSubtabs, v]);
            } catch { /* ignore */ }
        }
        setSubtabInput("");
    };

    const removeSubtab = (name) => setSubtabs(subtabs.filter((s) => s !== name));

    const addBlock = () => {
        const letter = nextBlockLetter(blocks);
        setBlocks([...blocks, emptyBlock(letter, 1)]);
    };
    const updateBlock = (i, b) => {
        // Cross-block exercise drop: BlockEditor signals via `__cross_block_drop__`
        // because it can't mutate the sibling block. The parent (us) owns both
        // blocks, so we splice the exercise out of the source and into the target
        // in one tick — keeping React's state model linear.
        if (b?.__cross_block_drop__) {
            const { sourceBlockIndex, sourceExerciseIndex, targetBlockIndex } = b.__cross_block_drop__;
            setBlocks((prev) => {
                if (sourceBlockIndex === targetBlockIndex) return prev;
                const next = prev.map((blk) => ({ ...blk, exercises: [...(blk.exercises || [])] }));
                const src = next[sourceBlockIndex];
                if (!src || !src.exercises?.[sourceExerciseIndex]) return prev;
                const [moved] = src.exercises.splice(sourceExerciseIndex, 1);
                if (!moved) return prev;
                next[targetBlockIndex].exercises.push(moved);
                // Re-key slot numbers in both blocks
                next[sourceBlockIndex].exercises = next[sourceBlockIndex].exercises.map((e, idx) => ({ ...e, slot: idx + 1 }));
                next[targetBlockIndex].exercises = next[targetBlockIndex].exercises.map((e, idx) => ({ ...e, slot: idx + 1 }));
                // Source block can't be empty — backfill with a fresh slot if it was the last one
                if (!next[sourceBlockIndex].exercises.length) {
                    next[sourceBlockIndex].exercises = [emptyExercise(1)];
                }
                return next;
            });
            return;
        }
        setBlocks(blocks.map((x, idx) => idx === i ? b : x));
    };
    const removeBlock = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));

    const moveBlock = (from, to) => {
        if (from === to || from < 0 || to < 0 || from >= blocks.length || to >= blocks.length) return;
        setBlocks((prev) => {
            const next = [...prev];
            const [m] = next.splice(from, 1);
            next.splice(to, 0, m);
            return next;
        });
    };

    /**
     * Phase 1 supersets/circuits rebuild: validation walks block.exercises[]
     * (each block can hold multiple exercises). Error coordinates now include
     * `slotIdx` so the jump-to-error helper can deep-link into the right
     * exercise slot within the right block.
     *
     * Shape: { message, blockIdx, slotIdx, levelIdx, setIdx, field, ... }
     */
    const validate = () => {
        if (!title.trim()) return { message: "Title is required.", titleMissing: true };
        if (!blocks.length) return { message: "Add at least one block." };
        for (let bi = 0; bi < blocks.length; bi += 1) {
            const b = blocks[bi];
            if (!b.name?.trim()) {
                return { message: `Block #${bi + 1} is missing a name.`, blockIdx: bi, blockNameMissing: true };
            }
            const exercises = b.exercises || [];
            if (!exercises.length) {
                return { message: `Block ${b.name}: add at least one exercise.`, blockIdx: bi };
            }
            for (let xi = 0; xi < exercises.length; xi += 1) {
                const ex = exercises[xi];
                if (!ex.levels?.length) {
                    return { message: `Block ${b.name} · slot ${xi + 1}: needs at least one level.`, blockIdx: bi, slotIdx: xi };
                }
                for (let li = 0; li < ex.levels.length; li += 1) {
                    const l = ex.levels[li];
                    if (!l.exercise_id) {
                        return { message: `Block ${b.name} · slot ${xi + 1} · L${li + 1}: pick an exercise.`, blockIdx: bi, slotIdx: xi, levelIdx: li, exerciseMissing: true };
                    }
                    if (!l.sets_data?.length) {
                        return { message: `Block ${b.name} · slot ${xi + 1} · L${li + 1}: needs at least one set.`, blockIdx: bi, slotIdx: xi, levelIdx: li };
                    }
                    for (let si = 0; si < l.sets_data.length; si += 1) {
                        const s = l.sets_data[si];
                        const missingField = !String(s.percent_max ?? "").trim()
                            ? "percent_max"
                            : !String(s.reps ?? "").trim()
                                ? "reps"
                                : !String(s.rest_mins ?? "").trim()
                                    ? "rest_mins"
                                    : null;
                        if (missingField) {
                            const friendly = { percent_max: "% max", reps: "reps", rest_mins: "rest" }[missingField];
                            return {
                                message: `Block ${b.name} · slot ${xi + 1} · L${li + 1} · row ${si + 1}: ${friendly} is empty. Tap to jump there.`,
                                blockIdx: bi, slotIdx: xi, levelIdx: li, setIdx: si, field: missingField,
                            };
                        }
                    }
                }
            }
        }
        return null;
    };

    // Scrolls the offending block into view AND switches its level tab so the
    // bad set is actually visible. Then focuses the bad input.
    const jumpToValidationError = (err) => {
        if (!err) return;
        if (err.titleMissing) {
            const el = document.querySelector('[data-testid="workout-title"]');
            if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus?.(); }
            return;
        }
        if (typeof err.blockIdx !== "number") return;
        // Make BlockEditor switch to the right level tab
        if (typeof err.levelIdx === "number") {
            setForceLevelByBlock((prev) => ({
                ...prev,
                [err.blockIdx]: { level: err.levelIdx, v: (prev[err.blockIdx]?.v || 0) + 1 },
            }));
        }
        // Scroll + focus after the level switch has rendered
        setTimeout(() => {
            const slotPart = typeof err.slotIdx === "number" ? `-slot-${err.slotIdx}` : "";
            const sel = err.field && typeof err.setIdx === "number"
                ? `[data-testid="block-${err.blockIdx}${slotPart}-level-${err.levelIdx}-set-${err.setIdx}-${ {percent_max:"percent", reps:"reps", rest_mins:"rest"}[err.field] }"]`
                : err.exerciseMissing
                    ? `[data-testid="block-${err.blockIdx}${slotPart}-level-${err.levelIdx}-pick-exercise"]`
                    : err.blockNameMissing
                        ? `[data-testid="block-${err.blockIdx}-name"]`
                        : `[data-testid="block-${err.blockIdx}"]`;
            const el = document.querySelector(sel);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.focus?.();
            }
        }, 80);
    };

    const save = async () => {
        const v = validate();
        if (v) {
            setValidationError(v);
            toast.error("Almost there", {
                description: v.message,
                action: { label: "Jump to it", onClick: () => jumpToValidationError(v) },
            });
            // Also auto-jump on the first click so coaches don't even have to tap the action
            jumpToValidationError(v);
            return;
        }
        setValidationError(null);
        setSaving(true);
        try {
            const body = {
                title: title.trim(),
                category,
                estimated_duration: estimatedDuration === "" ? null : Number(estimatedDuration),
                times_per_week: timesPerWeek === "" ? null : Number(timesPerWeek),
                tier_required: tierRequired,
                body_focus: category === "strength" && bodyFocus ? bodyFocus : null,
                subtabs,
                blocks: blocks.map((b, i) => ({
                    id: b.id || `block_${i}`,
                    name: b.name,
                    block_group: b.block_group || b.name?.[0] || "A",
                    isCircuit: !!b.isCircuit,
                    circuit_group: b.circuit_group || null,
                    link_type: b.link_type || null,
                    coach_note: b.coach_note || "",
                    // Phase 1 supersets/circuits rebuild: blocks send `exercises`.
                    // The backend mirrors the first slot into legacy `levels` so
                    // any pre-rebuild athlete view code keeps rendering correctly
                    // for standalone (single-exercise) blocks.
                    exercises: (b.exercises || []).map((ex, exIdx) => ({
                        slot: ex.slot || (exIdx + 1),
                        levels: ex.levels.map((l) => ({
                            level: l.level,
                            name: l.name || "",
                            exercise_id: l.exercise_id,
                            exercise_name: l.exercise_name,
                            rm_type: l.rm_type || "",
                            bw_multiplier: l.bw_multiplier ?? null,
                            advancement_type: l.advancement_type || null,
                            advancement_value: l.advancement_value ?? null,
                            based_off_exercise_id: l.based_off_exercise_id || null,
                            alt_exercise_id: l.alt_exercise_id || null,
                            // Preserve `set_num` exactly as the coach entered it
                            // — that's how sparse rounds (e.g. exercise runs in
                            // rounds 1 + 3 only) are expressed.
                            sets_data: l.sets_data.map((s, si) => ({
                                set_num: Number.isFinite(Number(s.set_num)) ? Number(s.set_num) : si + 1,
                                percent_max: String(s.percent_max),
                                reps: String(s.reps),
                                rest_mins: String(s.rest_mins),
                            })),
                        })),
                    })),
                })),
            };
            if (isEdit) {
                await axios.put(`${API}/coach/workouts/${workoutId}`, body, { withCredentials: true });
                toast.success("Saved");
            } else {
                const r = await axios.post(`${API}/coach/workouts`, body, { withCredentials: true });
                toast.success("Created");
                navigate(`/coach/workouts/${r.data.workout_id}/edit`, { replace: true });
            }
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    const subtabSuggestions = useMemo(
        () => availableSubtabs.filter((s) => !subtabs.includes(s) && (subtabInput ? s.toLowerCase().includes(subtabInput.toLowerCase()) : true)).slice(0, 8),
        [availableSubtabs, subtabs, subtabInput],
    );

    if (loading || !user || busy) {
        return <div className="min-h-screen bg-brand-obsidian flex items-center justify-center"><Loader2 className="w-7 h-7 text-brand-red animate-spin" /></div>;
    }

    return (
        <div data-testid="workout-builder" className="bg-brand-obsidian min-h-screen pb-32">
            <section className="pt-32 pb-8 border-b border-zinc-900">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <Link to="/coach/workouts" className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> Workouts
                    </Link>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3">{isEdit ? "Edit workout" : "New workout"}</div>

                    <input
                        type="text"
                        data-testid="workout-title"
                        placeholder="Workout title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full bg-transparent border-b border-zinc-800 px-1 py-3 font-display text-4xl md:text-5xl tracking-wider text-white focus:outline-none focus:border-brand-red placeholder:text-zinc-700"
                    />

                    <div className="grid sm:grid-cols-3 gap-4 mt-6">
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">Category</label>
                            <select data-testid="workout-category" value={category} onChange={(e) => { setCategory(e.target.value); setSubtabs([]); }} className={inputCls}>
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">Duration (min)</label>
                            <input type="number" min="0" data-testid="workout-duration" value={estimatedDuration} onChange={(e) => setEstimatedDuration(e.target.value)} className={inputCls} placeholder="45" />
                        </div>
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">Tier required</label>
                            <select
                                data-testid="workout-tier"
                                value={tierRequired}
                                onChange={(e) => setTierRequired(e.target.value)}
                                className={inputCls}
                            >
                                <option value="non_member">Non-member (free)</option>
                                <option value="member">Member</option>
                                <option value="member_plus">Member+</option>
                            </select>
                        </div>
                    </div>

                    {category === "strength" && (
                        <div className="mt-5">
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">
                                Body focus
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[
                                    { value: "", label: "Unspecified" },
                                    { value: "full_body", label: "Full Body" },
                                    { value: "lower_body", label: "Lower Body" },
                                    { value: "upper_body", label: "Upper Body" },
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        data-testid={`workout-body-focus-${opt.value || "none"}`}
                                        onClick={() => setBodyFocus(opt.value)}
                                        className={`text-[11px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors ${
                                            bodyFocus === opt.value
                                                ? "border-brand-red bg-brand-red/10 text-brand-red"
                                                : "border-zinc-800 bg-black text-zinc-300 hover:border-zinc-600"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-zinc-500 mt-1.5">
                                Powers the Full / Lower / Upper sub-tabs on the athlete Strength library.
                            </p>
                        </div>
                    )}

                    <div className="mt-5">
                        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">Subtabs</label>
                        <div className="flex flex-wrap items-center gap-1.5 border border-zinc-800 bg-black px-2 py-2">
                            {subtabs.map((s) => (
                                <span key={s} data-testid={`workout-subtab-${s}`} className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] uppercase border border-brand-red/40 bg-brand-red/10 text-brand-red px-2 py-1">
                                    <Tag className="w-3 h-3" /> {s}
                                    <button type="button" onClick={() => removeSubtab(s)} className="ml-1 hover:text-white"><X className="w-3 h-3" /></button>
                                </span>
                            ))}
                            <input
                                type="text"
                                value={subtabInput}
                                onChange={(e) => setSubtabInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); addSubtab(subtabInput); }
                                    else if (e.key === "Backspace" && !subtabInput && subtabs.length) {
                                        e.preventDefault(); setSubtabs(subtabs.slice(0, -1));
                                    }
                                }}
                                placeholder={subtabs.length ? "" : "Add a tag…"}
                                data-testid="workout-subtab-input"
                                className="flex-1 min-w-[140px] bg-transparent text-sm text-white placeholder:text-zinc-200 focus:outline-none px-2 py-1"
                            />
                        </div>
                        {subtabSuggestions.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {subtabSuggestions.map((s) => (
                                    <button key={s} type="button" onClick={() => addSubtab(s)} className="text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-200 hover:text-brand-red hover:border-brand-red px-2.5 py-1.5">+ {s}</button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-5xl mx-auto px-6 md:px-10 space-y-5">
                    {blocks.map((b, i) => (
                        <div
                            key={i}
                            data-testid={`block-wrapper-${i}`}
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.setData("text/plain", String(i));
                                e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                            onDrop={(e) => {
                                e.preventDefault();
                                const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                                if (Number.isFinite(from)) moveBlock(from, i);
                            }}
                        >
                            <BlockEditor
                                category={category}
                                block={b}
                                index={i}
                                onChange={(next) => { updateBlock(i, next); if (validationError) setValidationError(null); }}
                                onRemove={() => removeBlock(i)}
                                canRemove={blocks.length > 1}
                                onMoveUp={i > 0 ? () => moveBlock(i, i - 1) : null}
                                onMoveDown={i < blocks.length - 1 ? () => moveBlock(i, i + 1) : null}
                                hasError={!!validationError && validationError.blockIdx === i}
                                validationError={validationError?.blockIdx === i ? validationError : null}
                                forceActiveLevel={forceLevelByBlock[i]}
                            />
                        </div>
                    ))}
                    <button type="button" data-testid="add-block" onClick={addBlock} className="btn-ghost-tactical inline-flex">
                        <Plus className="w-4 h-4" /> Add block
                    </button>
                </div>
            </section>

            <div className="fixed bottom-0 left-0 right-0 z-40 bg-black/95 border-t border-zinc-900 backdrop-blur">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-3">
                    <Link to="/coach/workouts" className="btn-ghost-tactical">Cancel</Link>
                    <button type="button" data-testid="workout-save" onClick={save} disabled={saving} className="btn-tactical disabled:opacity-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? "Saving…" : (isEdit ? "Save workout" : "Create workout")}
                    </button>
                </div>
            </div>
        </div>
    );
}
